<template>
  <div>
    <p v-if='!loaded'>Enter domain to manage DomainKeys.</p>
    <div class='field is-grouped'>
      <div class='control is-expanded'>
        <input
          class='input'
          type='text'
          placeholder='Domain'
          ref='domainSearch'
          v-model='domain'
          @keyup.enter='onSearch'
        />
      </div>

      <div class='control'>
        <a class='button' @click='onSearch'>
          Search
        </a>
      </div>
    </div>

    <hr>

    <div :class='$style.buttonContainer' v-if='loaded && isDkimAdmin'>
      <button :class='[{ "is-loading": isCreating }, "button"]' @click='onClickCreateSelector'>
        Create Domain Selector
      </button>
    </div>

    <ErrorNotification v-if='errors.length' :messages='errors' @clear='errors = []' />

    <SelectorList
      v-if='selectors.length'
      :selectors='selectors'
      @rowClicked='onRowClicked'
    />

    <p v-if='!selectors.length && loaded'>No selectors found for this domain.</p>

    <SelectorDetails
      :isOpen='isDetailsOpen'
      :selected='selected'
      :isAdmin='isDkimAdmin'
      :errors='detailsErrors'
      :isValidating='isValidating'
      @close='closeDetails'
      @validate='onValidateSelector'
      @delete='onDeleteSelector'
    />

  </div>
</template>

<script>
import useUserHasPermission from '@/composables/useUserHasPermission';
import {
  createDomainSelector,
  validateDomainSelector,
  deleteDomainSelector,
  getSelectorsForDomain
} from '@/request.js';
import SelectorList from '@/components/SelectorList.vue';
import SelectorDetails from '@/components/SelectorDetails.vue';
import ErrorNotification from '@/components/ErrorNotification.vue';

const DKIM_ADMIN_PERMISSIONS = 'dkimAdmin';

export default {
  name: 'DomainKeys',

  components: {
    SelectorList,
    SelectorDetails,
    ErrorNotification
  },

  setup() {
    const { hasPermission } = useUserHasPermission(DKIM_ADMIN_PERMISSIONS);
    return {
      isDkimAdmin: hasPermission
    };
  },

  data() {
    const { domain } = this.$route.query;

    return {
      domain: domain || '',
      loaded: false,
      selectors: [],
      errors: [],
      detailsErrors: [],
      isDetailsOpen: false,
      selected: {},
      isCreating: false,
      isValidating: false
    }
  },

  async mounted() {
    if (this.domain) {
      this.fetchDomainKeys();
    } else {
      this.$nextTick(() => {
        this.$refs.domainSearch.focus();
      });
    }
  },

  methods: {
    async fetchDomainKeys() {
      try {
        this.selectors = await getSelectorsForDomain(this.domain) || [];
        this.errors = [];
        this.loaded = true;
      }
      catch (error) {
        this.errors = [error.message];
        this.selectors = [];
        this.loaded = false;
      }
    },

    async onClickCreateSelector() {
      try {
        this.isCreating = true;
        await createDomainSelector(this.domain);
        this.fetchDomainKeys();
        this.isCreating = false;
      }
      catch (error) {
        this.errors = [error.message];
        this.selectors = [];
      }
    },

    async onValidateSelector() {
      this.isValidating = true;

      try {
        const validatedDomain = await validateDomainSelector(this.domain, this.selected.selector);
        this.selected.validatedAt = validatedDomain.validatedAt;
        this.detailsErrors = [];
      }
      catch (error) {
        this.detailsErrors = [error.message];
      }

      this.isValidating = false;
    },

    async onDeleteSelector() {
      try {
        await deleteDomainSelector(this.domain, this.selected.selector);
        this.fetchDomainKeys();
        this.closeDetails();
      }
      catch (error) {
        this.detailsErrors = [error.message];
      }
    },

    onSearch() {
      if (!this.domain.trim().length) {
        return;
      }

      this.$router.push({
        path: '/domainkeys',
        query: { domain: this.domain }
      });

      this.fetchDomainKeys();
    },

    onRowClicked(selector) {
      this.isDetailsOpen = true;
      this.selected = selector;
    },

    closeDetails() {
      this.isDetailsOpen = false;
      this.selected = {};
      this.detailsErrors = [];
    }
  }
}
</script>

<style lang="scss" module>
.buttonContainer {
  text-align: center;

  button {
    margin: 0 10px;
  }
}

.tableContainer {
  text-align: center;
}
</style>
